import { useNavigate, useSearch } from '@tanstack/react-router';
import { OnChangeValue } from 'react-select';

import { NoFilterResults } from '../../cdl/Filter/NoFilterResults/NoFilterResults';
import { StatePlaceholder } from '../../cdl/StatePlaceholder/StatePlaceholder';
import { Text } from '../../cdl/Text/Text';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { CustomerGroupSelect } from '../../common/form-elements/CustomerGroupSelect/CustomerGroupSelect';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { IconGameplan } from '../../common/icons/cdl/Gameplan';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { Page } from '../../common/Page/Page';
import { Box } from '../../common/ui/Box';
import { DemandAccordion } from '../components/DemandAccordion';
import { DemandReachBanner } from '../components/DemandReachBanner';
import { DemandReportButton } from '../components/DemandReportButton';
import { DemandSortingSegmentedControl } from '../components/DemandSortingSegmentedControl';
import { VesselDemandFilter } from '../components/VesselDemandFilter';
import { useDemandInformation } from '../hooks/useDemandInformation';

const DemandOverviewLoading = ({ showEmptyPagePlaceholder }: { showEmptyPagePlaceholder: boolean }) => {
    if (showEmptyPagePlaceholder) {
        return (
            <Box display="flex" justifyContent="center" paddingTop="88px">
                <StatePlaceholder
                    title={translate('demand.emptyPageAdmin.title')}
                    description={translate('demand.emptyPageAdmin.subtitle')}
                    Icon={IconGameplan}
                />
            </Box>
        );
    }

    return <LoadingIndicator variant="page" />;
};

export const DemandOverviewPage = () => {
    useDocumentTitle(translate('demand.demandOverview'));
    const role = useRole();
    const search = useSearch({ from: '/_app/demand' });
    const navigate = useNavigate({ from: '/demand' });

    const demandInformationQuery = useDemandInformation(search.sortingKey, search.groupId);

    const customerGroupId = role.isSuperAdmin() ? search.groupId : role.group?.id;

    const onGroupChange = (option: OnChangeValue<string, false>) => {
        if (option !== null) {
            navigate({ search: (previousSearch) => ({ ...previousSearch, groupId: option }) });
        }
    };

    return (
        <Page>
            <Box display="flex" justifyContent="space-between" columnGap={24} marginBottom={16}>
                <Text
                    as="h1"
                    variant="headline"
                    style={{
                        whiteSpace: 'nowrap',
                    }}
                >
                    {translate('demand.demandOverview')}
                </Text>

                <Box display="flex" alignItems="center" justifyContent="space-between" columnGap={8}>
                    {role.isSuperAdmin() ? (
                        <Box width="300px">
                            <CustomerGroupSelect
                                onChange={onGroupChange}
                                value={search.groupId}
                                placeholder={translate('demand.groupSelectPlaceholder')}
                                dropdownWidth="520px"
                            />
                        </Box>
                    ) : null}
                    <DemandReportButton customerGroupId={customerGroupId} />
                </Box>
            </Box>

            {demandInformationQuery.isPending ? (
                <DemandOverviewLoading showEmptyPagePlaceholder={role.isSuperAdmin() && !search.groupId} />
            ) : null}
            {demandInformationQuery.isError ? <AdvancedError /> : null}

            {demandInformationQuery.isSuccess ? (
                <>
                    {demandInformationQuery.data.vessels.every((vessel) => !vessel.categories.length) &&
                    !role.isSuperAdmin() ? (
                        <DemandReachBanner />
                    ) : null}

                    <Box marginTop={16}>
                        <VesselDemandFilter
                            allEntries={demandInformationQuery.data.vessels}
                            groupId={search.groupId}
                            headerAction={
                                <DemandSortingSegmentedControl
                                    value={search.sortingKey}
                                    onChange={(sortingKey) => {
                                        navigate({
                                            search: (previousSearch) => ({ ...previousSearch, sortingKey }),
                                        });
                                    }}
                                />
                            }
                        >
                            {(filteredItems) => {
                                if (filteredItems.length === 0) {
                                    return <NoFilterResults />;
                                }

                                return (
                                    <Box display="grid" rowGap={16}>
                                        {filteredItems.map((vessel) => {
                                            if (!vessel.categories.length) {
                                                return <DemandAccordion key={vessel.id} vessel={vessel} disabled />;
                                            }

                                            return <DemandAccordion key={vessel.id} vessel={vessel} />;
                                        })}
                                    </Box>
                                );
                            }}
                        </VesselDemandFilter>
                    </Box>
                </>
            ) : null}
        </Page>
    );
};
